import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Collapse,
  Icon,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import { Manager, Popper, Reference } from 'react-popper';
import HorizontalItem from './HorizontalItem';
import HorizontalGroup from './HorizontalGroup';
// import IntlMessages from '@crema/helpers/IntlMessages';
// import { useThemeContext } from '@crema/context/AppContextProvider/ThemeContextProvider';
// import { RouterConfigData } from '@crema/types/models/Apps';
import { NavItemProps as RouterConfigData } from 'modules/routesConfig';
import ClientOnlyPortal from './ClientPortal';
// import { useSidebarContext } from '@crema/context/AppContextProvider/SidebarContextProvider';
import AppContext from '@crema/utility/AppContext';
import IntlMessages from '@crema/utility/IntlMessages';
import { isUrlInChildren, navListItemStyle } from '.';
import { checkPermission } from '@crema/utility/Utils';

type HorizontalCollapseProps = {
  item: RouterConfigData;
  nestedLevel: number;
  dense?: number;
};

const HorizontalCollapse: React.FC<HorizontalCollapseProps> = (props) => {
  const location = useLocation();
  const { item, nestedLevel, dense } = props;
  const [opened, setOpened] = useState<boolean>(false);
  const isMenuTop = useMemo(() => nestedLevel === 0, [nestedLevel])
  // const { theme } = useThemeContext();
  const { theme, sidebarMenuSelectedBgColor, sidebarMenuSelectedTextColor, HPUser } = useContext(AppContext)
  const hasPermission = useMemo(
    () => checkPermission(item.auth, HPUser!.ruoli),
    [
      item.auth,
      HPUser,
    ],
    );

  const active = isUrlInChildren(item, location.pathname);
  // const { sidebarMenuSelectedBgColor, sidebarMenuSelectedTextColor } =
  //   useSidebarContext();

  //delayedOpened serve per evitare che il menu si apra senza animazione
  const [delayedOpened, setDelayedOpened] = useState(false);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (opened) {
      timer = setTimeout(() => {
        setDelayedOpened(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setDelayedOpened(false);
    }

    return () => clearTimeout(timer);
  }, [opened]);


  const handleToggle = (open: boolean) => {
    setOpened(open);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <List
      sx={{
        py: 0,
        '& .list-item-text': {
          padding: '0 0 0 16px',
        },
        ...navListItemStyle,
        alignItems: 'stretch',
      }}
      className='navbarNavSubmenu'>
      <Manager>
        <Reference>
          {({ref}) => (
            <ListItem
              ref={ref}
              sx={{
                color: theme.palette.text.primary,
                padding: '4px 12px',
                position: 'relative', // Ensure the pseudo-element is positioned correctly
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: 'none',
                  borderRadius: 'inherit',
                },
                '&:hover::before': {
                  opacity: 1,
                },
                '&.active, &.active:hover, &.active:focus': {
                  backgroundColor: sidebarMenuSelectedBgColor + '!important',
                  color: sidebarMenuSelectedTextColor + '!important',
                  '&::before': {
                    opacity: 0,
                  },
                },
                '&.open': {
                  '&::before': {
                    opacity: 1,
                  },
                },
                '&.dense': {
                  padding: '0px 12px',
                  '& .list-item-text': {
                    padding: '0 0 0 8px',
                  },
                },
                ...navListItemStyle,
              }}
              className={clsx(
                'navItemSubmenu',
                opened && 'open',
                dense && 'dense',
                active && 'active',
              )}
              onMouseEnter={() => handleToggle(true)}
              onMouseLeave={() => handleToggle(false)}>
              {!isMenuTop && item.icon && (
                <Icon
                  sx={{
                    // color: active ? sidebarMenuSelectedTextColor : 'action',
                    color: active ? sidebarMenuSelectedTextColor : theme.palette.sidebar.textColor,
                    // mr: 3.5,
                    mr: '8px',
                    fontSize: {xs: 16, xl: 18},
                  }}>
                  {item.icon}
                </Icon>
              )}
              <ListItemText
                className='navLinkTextSubmenu'
                primary={<IntlMessages id={item.messageId} />}
                primaryTypographyProps={{
                  color: active ? sidebarMenuSelectedTextColor : theme.palette.sidebar.textColor,
                  fontWeight: 500,
                  noWrap: true,
                }}
              />
              <Box p={0}>
                {!isMenuTop && (
                  // <IconButton
                  //   // disabilita effetto ripple
                  //   sx={{
                  //     ':hover': {
                  //       backgroundColor: 'transparent',
                  //     },
                  //   }}>
                    <Icon
                      sx={{
                        color: active ? sidebarMenuSelectedTextColor : 'action',
                      }}>
                      {theme.direction === 'ltr'
                        ? 'chevron_right'
                        : 'chevron_left'}
                    </Icon>
                  // </IconButton>
                )}
              </Box>
            </ListItem>
          )}
        </Reference>
        <ClientOnlyPortal selector='#root'>
          <Popper placement={isMenuTop ? 'bottom-start' : 'right-start'}>
            {({ref, style, placement}) =>
              opened && (
                <Box
                  ref={ref}
                  sx={{
                    boxShadow: '0 0 3px 0 rgba(0, 0, 0, 0.2)',
                    zIndex: 1110 + nestedLevel + 1,
                    ...style,
                    '& .popperClose': {
                      pointerEvents: 'none',
                    },
                    borderRadius: '8px'
                  }}
                  data-placement={placement}
                  className={clsx({
                    popperClose: !opened,
                  })}>
                  {/* <Grow in={opened}> */}
                  {/* <Collapse in={opened} */}
                  <Collapse in={delayedOpened}
                    orientation='vertical'
                  >
                    <Paper
                      onMouseEnter={() => handleToggle(true)}
                      onMouseLeave={() => handleToggle(false)}
                      sx={{borderRadius: '8px'}}
                    >
                      {item.children && (
                        <List
                          sx={{
                            // p: 0,
                          }}>
                          {item.children.map((item) => (
                            <React.Fragment key={item.id}>
                              {item.type === 'group' && (
                                <HorizontalGroup
                                  item={item}
                                  nestedLevel={nestedLevel + 1}
                                />
                              )}

                              {item.type === 'collapse' && (
                                <HorizontalCollapse
                                  item={item}
                                  nestedLevel={nestedLevel + 1}
                                />
                              )}

                              {item.type === 'item' && (
                                <HorizontalItem
                                  item={item}
                                  nestedLevel={nestedLevel + 1}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </Paper>
                  </Collapse>
                </Box>
              )
            }
          </Popper>
        </ClientOnlyPortal>
      </Manager>
    </List>
  );
};

export default HorizontalCollapse;
